import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { formatTL } from "../lib/format";
import {
  BELGE_TURLERI,
  KANALLAR,
  KAYIT_TIPLERI,
  BOLUMLER,
  ALT_HIZMETLER,
  ODEME_YERLERI,
  GELIR_TURLERI,
  HIZMET_TURLERI,
  SSH,
  bugun,
} from "../lib/sabitler";
import { Switch } from "./ui/switch";
import { Loader2, Banknote, Plus, FileText, Car, Layers, Briefcase, Receipt, X } from "lucide-react";

const EMPTY = {
  tarih: "",
  vade_tarihi: "",
  kayit_tipi: "Fatura",
  kdv_haric_tahsilat: false,
  gelir_turu: "Araç",
  belge_turu: "",
  matrah: "",
  kdv: "",
  musteri_aciklama: "",
  sigorta_sirketi: "",
  plaka: "",
  plaka_dosya_no: "",
  belge_no: "",
  is_emri_no: "",
  hizmet_turu: "",
  hizmet_aciklama: "",
  proje_sozlesme_no: "",
  kanal: "",
  bolum: "",
  alt_hizmet: "",
  tahsilat_durumu: "Bekliyor",
  tahsilat_tarihi: "",
  fatura_pdf_id: null,
  aninda_tahsilat: false,
  tahsilat_alan_kisi: "",
  tahsilat_odeme_yeri: "",
  tahsilat_tedarikci: "",
  ustalar: [],
  dis_kaynak: [],
};

const GELIR_TURU_ICON = { "Araç": Car, "Hizmet": Briefcase, "Diğer": Receipt };

/* Bölüm başlığı — formu yatayda profesyonel gruplara ayırır */
const BolumBaslik = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 border-b border-border/60 pb-2">
    <Icon className="h-4 w-4 text-[hsl(var(--primary))]/70" />
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</span>
  </div>
);

export const CiroForm = ({ initial, onSubmit, submitting, submitLabel = "Kaydet" }) => {
  const editMode = Boolean(initial?.id);
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    tarih: bugun(),
    ...(initial
      ? {
          ...initial,
          matrah: initial.matrah != null ? String(initial.matrah) : "",
          kdv: initial.kdv != null ? String(initial.kdv) : "",
          musteri_aciklama: initial.musteri_aciklama || "",
          sigorta_sirketi: initial.sigorta_sirketi || "",
          plaka: initial.plaka || "",
          // Dosya No: tek alan — eski "Hasar Dosya No" değerleri de burada birleşir
          plaka_dosya_no: initial.plaka_dosya_no || initial.hasar_dosya_no || "",
          belge_no: initial.belge_no || "",
          is_emri_no: initial.is_emri_no || "",
          gelir_turu: initial.gelir_turu || "Araç",
          hizmet_turu: initial.hizmet_turu || "",
          hizmet_aciklama: initial.hizmet_aciklama || "",
          proje_sozlesme_no: initial.proje_sozlesme_no || "",
          kayit_tipi: initial.kayit_tipi || "Fatura",
          kdv_haric_tahsilat: !!initial.kdv_haric_tahsilat,
          vade_tarihi: initial.vade_tarihi || "",
          kanal: initial.kanal || "",
          bolum: initial.bolum || "",
          alt_hizmet: initial.alt_hizmet || "",
          tahsilat_durumu: initial.tahsilat_durumu || "Bekliyor",
          tahsilat_tarihi: initial.tahsilat_tarihi || "",
          fatura_pdf_id: initial.fatura_pdf_id || null,
          ustalar: initial.ustalar || [],
          dis_kaynak: (initial.dis_kaynak || []).map((d) => ({
            ...d,
            matrah: String(d.matrah ?? d.tutar ?? ""),
            kdv: String(d.kdv ?? 0),
            dosya_adi: d.dosya_adi || (d.fatura_dosya_id ? "Yüklü fatura" : ""),
          })),
          tarih: initial.tarih || bugun(),
        }
      : {}),
  }));

  // Tedarikçi listesi (Tedarikçi Ödemesi seçildiğinde yüklenir)
  const [tedarikciler, setTedarikciler] = useState([]);
  const [yeniTedarikci, setYeniTedarikci] = useState(false);
  useEffect(() => {
    if (form.tahsilat_odeme_yeri === "Tedarikçi Ödemesi" && tedarikciler.length === 0) {
      api.get("/tedarikciler").then((res) => {
        setTedarikciler(res.data || []);
        if ((res.data || []).length === 0) setYeniTedarikci(true);
      }).catch(() => setYeniTedarikci(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tahsilat_odeme_yeri]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Usta listesi (Personel modülünden: aktif personel + çalıştığı bölümler)
  const [ustaListesi, setUstaListesi] = useState(null);
  useEffect(() => {
    api.get("/ustalar").then((res) => setUstaListesi(res.data || [])).catch(() => setUstaListesi([]));
  }, []);

  const toggleUsta = (u) => {
    setForm((f) => {
      const secili = f.ustalar.some((x) => x.id === u.id);
      return { ...f, ustalar: secili ? f.ustalar.filter((x) => x.id !== u.id) : [...f.ustalar, { id: u.id, ad_soyad: u.ad_soyad, bolum: u.bolum || "" }] };
    });
  };

  // Bölüm dağılımı (bir araçta birden fazla bölüm + bölüm içi usta kırılımı)
  const aiBolumler = initial?.tahmini_bolumler || [];
  const [multiBolum, setMultiBolum] = useState(
    () => (initial?.bolum_dagilimi || []).length > 0 || aiBolumler.length > 1
  );
  const [bolumPaylar, setBolumPaylar] = useState(() => {
    const mevcut = initial?.bolum_dagilimi || [];
    if (mevcut.length > 0) {
      return mevcut.map((p) => ({
        bolum: p.bolum,
        tutar: String(p.tutar),
        ustalar: (p.ustalar || []).map((u) => ({ ...u, tutar: String(u.tutar || "") })),
      }));
    }
    if (aiBolumler.length > 1) {
      return aiBolumler.map((b) => ({ bolum: b, tutar: "", ustalar: [] }));
    }
    return [{ bolum: "", tutar: "", ustalar: [] }, { bolum: "", tutar: "", ustalar: [] }];
  });
  const paySet = (i, key, value) =>
    setBolumPaylar((arr) => arr.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  const payUstaToggle = (i, u) =>
    setBolumPaylar((arr) =>
      arr.map((p, idx) => {
        if (idx !== i) return p;
        const secili = p.ustalar.some((x) => x.id === u.id);
        return {
          ...p,
          ustalar: secili
            ? p.ustalar.filter((x) => x.id !== u.id)
            : [...p.ustalar, { id: u.id, ad_soyad: u.ad_soyad, tutar: "" }],
        };
      })
    );
  const payUstaTutar = (i, ustaId, value) =>
    setBolumPaylar((arr) =>
      arr.map((p, idx) =>
        idx === i ? { ...p, ustalar: p.ustalar.map((x) => (x.id === ustaId ? { ...x, tutar: value } : x)) } : p
      )
    );
  const bolumUstalari = (bolum) => {
    if (!ustaListesi) return [];
    return ustaListesi.filter((u) => (u.bolumler || []).includes(bolum));
  };

  // Dış Kaynak satırları (Matrah + KDV ayrı; faturalıysa fatura dosyası zorunlu)
  const dkSet = (i, key, value) =>
    setForm((f) => ({ ...f, dis_kaynak: f.dis_kaynak.map((d, idx) => (idx === i ? { ...d, [key]: value } : d)) }));
  const dkEkle = () =>
    setForm((f) => ({
      ...f,
      dis_kaynak: [...f.dis_kaynak, { hizmet_adi: "", tedarikci: "", matrah: "", kdv: "", faturali: true, fatura_dosya_id: null, dosya_adi: "" }],
    }));
  const dkSil = (i) => setForm((f) => ({ ...f, dis_kaynak: f.dis_kaynak.filter((_, idx) => idx !== i) }));
  const dkDosyaYukle = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    dkSet(i, "_yukleniyor", true);
    try {
      const res = await api.post("/dosya-yukle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        dis_kaynak: f.dis_kaynak.map((d, idx) =>
          idx === i ? { ...d, fatura_dosya_id: res.data.dosya_id, dosya_adi: res.data.dosya_adi, _yukleniyor: false } : d
        ),
      }));
      toast.success("Dış kaynak faturası yüklendi.");
    } catch (err) {
      dkSet(i, "_yukleniyor", false);
      toast.error(getErrMsg(err, "Fatura yüklenemedi."));
    }
  };

  const isGR = form.belge_turu === "G.R";
  const isSSH = form.bolum === SSH;
  const isHizmet = form.gelir_turu === "Hizmet";
  const isArac = form.gelir_turu === "Araç";

  const toplam = useMemo(() => {
    const m = parseFloat(form.matrah) || 0;
    const k = isGR ? 0 : parseFloat(form.kdv) || 0;
    return m + k;
  }, [form.matrah, form.kdv, isGR]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    if (!editMode && form.kayit_tipi !== "İş Emri" && !form.vade_tarihi) return toast.error("Vade tarihi zorunludur.");
    if (!form.belge_turu) return toast.error("Belge türü seçin.");
    const matrah = parseFloat(form.matrah);
    if (!matrah || matrah <= 0) return toast.error("Matrah 0'dan büyük olmalıdır.");
    if (!form.kanal) return toast.error("Kanal seçin.");
    if (isHizmet && !form.hizmet_turu) return toast.error("Hizmet türü seçin.");

    // Bölüm dağılımı doğrulama (toplam matrahı GEÇEMEZ; bölüm içi usta dağılımı bölüm tutarını geçemez)
    let anaBolum = form.bolum;
    let dagilimPayload = null;
    if (!isHizmet && multiBolum) {
      const dolu = bolumPaylar.filter((p) => p.bolum && parseFloat(p.tutar) > 0);
      if (dolu.length < 1) return toast.error("Bölüm dağılımı için en az bir bölüm ve tutar girin (veya tek bölüm modunu kullanın).");
      const bolumSet = new Set(dolu.map((p) => p.bolum));
      if (bolumSet.size !== dolu.length) return toast.error("Aynı bölüm dağılımda iki kez seçilemez.");
      const payToplam = dolu.reduce((s, p) => s + parseFloat(p.tutar), 0);
      if (payToplam - matrah > 0.01)
        return toast.error(`Bölüm dağılımı toplamı (${payToplam.toLocaleString("tr-TR")}) fatura matrahını (${matrah.toLocaleString("tr-TR")}) geçemez.`);
      for (const p of dolu) {
        const ustaToplam = p.ustalar.reduce((s, u) => s + (parseFloat(u.tutar) || 0), 0);
        if (ustaToplam - parseFloat(p.tutar) > 0.01)
          return toast.error(`${p.bolum} bölümünde usta dağılımı toplamı (${ustaToplam.toLocaleString("tr-TR")}) bölüm tutarını geçemez.`);
      }
      dagilimPayload = dolu.map((p) => ({
        bolum: p.bolum,
        tutar: parseFloat(p.tutar),
        ustalar: p.ustalar.map((u) => ({ id: u.id, ad_soyad: u.ad_soyad, tutar: parseFloat(u.tutar) || 0 })),
      }));
      anaBolum = dolu.reduce((mx, p) => (parseFloat(p.tutar) > parseFloat(mx.tutar) ? p : mx), dolu[0]).bolum;
    } else if (!isHizmet && !form.bolum) {
      return toast.error("Bölüm seçin.");
    }
    const kirilimUstaVar = (dagilimPayload || []).some((p) => p.ustalar.length > 0);
    if (!editMode && isArac) {
      if (multiBolum && !kirilimUstaVar)
        return toast.error("Kırılımda en az bir bölüme usta seçin.");
      if (!multiBolum && form.ustalar.length === 0)
        return toast.error("Bu işte çalışan en az bir usta seçilmelidir.");
    }
    const efektifSSH = !isHizmet && (multiBolum ? anaBolum === SSH : isSSH);
    if (efektifSSH && !form.alt_hizmet)
      return toast.error("Satış Sonrası Hizmetler için Alt Hizmet seçimi zorunludur.");

    // Dış Kaynak doğrulama (boş satırlar atlanır; faturalı satırda fatura dosyası zorunlu)
    const dkPayload = [];
    if (!isHizmet) {
      for (const d of form.dis_kaynak) {
        const bos = !d.hizmet_adi.trim() && !(parseFloat(d.matrah) > 0) && !d.tedarikci.trim();
        if (bos) continue;
        if (!d.hizmet_adi.trim()) return toast.error("Dış kaynak satırında hizmet adı zorunludur.");
        if (!(parseFloat(d.matrah) > 0)) return toast.error(`"${d.hizmet_adi}" dış kaynak satırında matrah 0'dan büyük olmalıdır.`);
        if (d.faturali && !d.fatura_dosya_id)
          return toast.error(`"${d.hizmet_adi}" satırı faturalı — fatura dosyasının yüklenmesi zorunludur.`);
        dkPayload.push({
          id: d.id,
          hizmet_adi: d.hizmet_adi.trim(),
          tedarikci: d.tedarikci.trim(),
          matrah: parseFloat(d.matrah),
          kdv: parseFloat(d.kdv) || 0,
          faturali: !!d.faturali,
          fatura_dosya_id: d.fatura_dosya_id || null,
        });
      }
    }
    if (!editMode && form.aninda_tahsilat) {
      if (!form.tahsilat_alan_kisi.trim())
        return toast.error("Anında tahsilat için tahsilatı alan kişi zorunludur.");
      if (!form.tahsilat_odeme_yeri)
        return toast.error("Anında tahsilat için nereye ödendiğini seçin.");
      if (form.tahsilat_odeme_yeri === "Tedarikçi Ödemesi" && !form.tahsilat_tedarikci.trim())
        return toast.error("Tedarikçi ödemesi için tedarikçi adı zorunludur.");
    }

    onSubmit({
      tarih: form.tarih,
      vade_tarihi: form.vade_tarihi || "",
      kayit_tipi: isArac ? (form.kayit_tipi || "Fatura") : "Fatura",
      kdv_haric_tahsilat:
        (form.kayit_tipi === "İş Emri" || form.belge_turu === "Proforma Fatura") ? !!form.kdv_haric_tahsilat : false,
      gelir_turu: form.gelir_turu || "Araç",
      belge_turu: form.belge_turu,
      musteri_aciklama: form.musteri_aciklama.trim(),
      sigorta_sirketi: isHizmet ? "" : form.sigorta_sirketi.trim(),
      // Tek alan: Dosya No — hasar dosya no ile birleştirildi
      hasar_dosya_no: null,
      plaka: isHizmet ? null : (form.plaka.trim() || null),
      plaka_dosya_no: isHizmet ? null : (form.plaka_dosya_no.trim() || null),
      belge_no: form.belge_no.trim(),
      is_emri_no: isHizmet ? "" : form.is_emri_no.trim(),
      hizmet_turu: isHizmet ? form.hizmet_turu : null,
      hizmet_aciklama: isHizmet ? form.hizmet_aciklama.trim() : "",
      proje_sozlesme_no: isHizmet ? (form.proje_sozlesme_no.trim() || null) : null,
      matrah,
      kdv: isGR ? 0 : parseFloat(form.kdv) || 0,
      kanal: form.kanal,
      bolum: isHizmet ? "Diğer" : anaBolum,
      bolum_dagilimi: dagilimPayload,
      alt_hizmet: efektifSSH ? form.alt_hizmet : null,
      tahsilat_durumu: form.tahsilat_durumu,
      tahsilat_tarihi:
        !editMode && form.aninda_tahsilat
          ? form.tahsilat_tarihi || form.tarih
          : form.tahsilat_durumu !== "Bekliyor" && form.tahsilat_tarihi
          ? form.tahsilat_tarihi
          : null,
      fatura_pdf_id: form.fatura_pdf_id || null,
      ustalar: isHizmet || multiBolum ? [] : form.ustalar,
      dis_kaynak: dkPayload,
      aninda_tahsilat: !editMode && form.aninda_tahsilat,
      tahsilat_alan_kisi: !editMode && form.aninda_tahsilat ? form.tahsilat_alan_kisi.trim() : null,
      tahsilat_odeme_yeri: !editMode && form.aninda_tahsilat ? form.tahsilat_odeme_yeri : null,
      tahsilat_tedarikci:
        !editMode && form.aninda_tahsilat && form.tahsilat_odeme_yeri === "Tedarikçi Ödemesi"
          ? form.tahsilat_tedarikci.trim()
          : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="ciro-form">
      {/* ---- GELİR TÜRÜ SEÇİMİ ---- */}
      <div className="space-y-3">
        <BolumBaslik icon={Receipt}>Gelir Türü</BolumBaslik>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ciro-form-gelir-turu-secimi">
          {GELIR_TURLERI.map((g) => {
            const Icon = GELIR_TURU_ICON[g.value];
            const secili = form.gelir_turu === g.value;
            return (
              <button
                key={g.value}
                type="button"
                onClick={() => set("gelir_turu", g.value)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] ${
                  secili
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 shadow-[inset_0_0_0_1px_hsl(var(--primary))]"
                    : "border-border bg-card hover:bg-muted/40"
                }`}
                data-testid={`ciro-form-gelir-turu-${g.value === "Araç" ? "arac" : g.value === "Hizmet" ? "hizmet" : "diger"}`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${secili ? "bg-[hsl(var(--primary))] text-[hsl(var(--accent))]" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span>
                  <span className={`block text-sm font-semibold ${secili ? "text-[hsl(var(--primary))]" : ""}`}>{g.label}</span>
                  <span className="block text-[11px] text-muted-foreground">
                    {g.value === "Araç" ? "Araç onarım / servis geliri" : g.value === "Hizmet" ? "Danışmanlık, eğitim, kiralama vb." : "Diğer gelir kalemleri"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- BELGE & TUTAR BİLGİLERİ ---- */}
      <div className="space-y-3">
        <BolumBaslik icon={FileText}>Belge ve Tutar Bilgileri</BolumBaslik>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {isArac && (
          <div className="space-y-1.5">
            <Label>Kayıt Tipi *</Label>
            <Select value={form.kayit_tipi} onValueChange={(v) => set("kayit_tipi", v)}>
              <SelectTrigger className="h-12" data-testid="ciro-form-kayit-tipi-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {KAYIT_TIPLERI.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
          <div className="space-y-1.5">
            <Label>Belge Türü *</Label>
            <Select value={form.belge_turu} onValueChange={(v) => set("belge_turu", v)}>
              <SelectTrigger className="h-12" data-testid="ciro-form-belge-turu-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {BELGE_TURLERI.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="whitespace-nowrap">Fatura Tarihi *</Label>
            <Input
              type="date"
              value={form.tarih}
              onChange={(e) => set("tarih", e.target.value)}
              className="h-12"
              data-testid="ciro-form-tarih-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Vade Tarihi {!editMode && "*"}</Label>
            <Input
              type="date"
              value={form.vade_tarihi}
              onChange={(e) => set("vade_tarihi", e.target.value)}
              className="h-12"
              data-testid="ciro-form-vade-tarihi-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="whitespace-nowrap">Matrah (KDV hariç) * ₺</Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              placeholder="0,00"
              value={form.matrah}
              onChange={(e) => set("matrah", e.target.value)}
              className="h-12 text-right tabular-nums"
              data-testid="ciro-form-matrah-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>KDV ₺ {isGR && <span className="text-xs text-muted-foreground">(G.R belgede 0 ve kilitli)</span>}</Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              placeholder="0,00"
              value={isGR ? "0" : form.kdv}
              disabled={isGR}
              onChange={(e) => set("kdv", e.target.value)}
              className="h-12 text-right tabular-nums disabled:opacity-60"
              data-testid="ciro-form-kdv-input"
            />
          </div>
        </div>

        {/* Canlı toplam */}
        <div
          className="flex items-center justify-between rounded-lg bg-[hsl(var(--primary))] px-4 py-3 text-white"
          data-testid="ciro-form-toplam-display"
        >
          <span className="text-sm text-white/70">Toplam (Matrah + KDV)</span>
          <span className="font-display text-xl font-bold tabular-nums text-[hsl(var(--accent))]">
            {formatTL(toplam, 2)}
          </span>
        </div>

        {/* KDV'siz tahsilat — yalnızca İş Emri / Proforma */}
        {(form.kayit_tipi === "İş Emri" || form.belge_turu === "Proforma Fatura") && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-4 py-3" data-testid="ciro-form-kdvsiz-alani">
            <div>
              <Label className="text-sm font-medium">KDV'siz Tahsilat (fatura kesilmeyecek)</Label>
              <p className="text-xs text-muted-foreground">
                Açıksa tahsilat hedefi KDV dahil toplam yerine <strong>matrah</strong> olur: {formatTL(parseFloat(form.matrah) || 0, 2)}
              </p>
            </div>
            <Switch
              checked={!!form.kdv_haric_tahsilat}
              onCheckedChange={(v) => set("kdv_haric_tahsilat", v)}
              data-testid="ciro-form-kdvsiz-switch"
            />
          </div>
        )}
      </div>

      {/* ---- MÜŞTERİ / HİZMET & DOSYA BİLGİLERİ ---- */}
      <div className="space-y-3">
        <BolumBaslik icon={isHizmet ? Briefcase : Car}>
          {isHizmet ? "Hizmet ve Müşteri Bilgileri" : isArac ? "Müşteri, Araç ve Dosya Bilgileri" : "Müşteri ve Belge Bilgileri"}
        </BolumBaslik>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{isHizmet ? "Hizmet Verilen Firma / Kişi" : "Müşteri / Açıklama"}</Label>
            <Input
              placeholder={isHizmet ? "Firma veya kişi adı" : "Müşteri adı veya açıklama"}
              value={form.musteri_aciklama}
              onChange={(e) => set("musteri_aciklama", e.target.value)}
              className="h-12"
              data-testid="ciro-form-musteri-input"
            />
          </div>
          {isHizmet && (
            <>
              <div className="space-y-1.5">
                <Label>Hizmet Türü *</Label>
                <Select value={form.hizmet_turu} onValueChange={(v) => set("hizmet_turu", v)}>
                  <SelectTrigger className="h-12" data-testid="ciro-form-hizmet-turu-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {HIZMET_TURLERI.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Hizmet Açıklaması</Label>
                <Input
                  placeholder="Verilen hizmetin kısa açıklaması"
                  value={form.hizmet_aciklama}
                  onChange={(e) => set("hizmet_aciklama", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-hizmet-aciklama-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Proje / Sözleşme No</Label>
                <Input
                  placeholder="PRJ-2026-001"
                  value={form.proje_sozlesme_no}
                  onChange={(e) => set("proje_sozlesme_no", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-proje-sozlesme-input"
                />
              </div>
            </>
          )}
          {isArac && (
            <>
              <div className="space-y-1.5">
                <Label>Sigorta Şirketi</Label>
                <Input
                  placeholder="Örn: Anadolu Sigorta"
                  value={form.sigorta_sirketi}
                  onChange={(e) => set("sigorta_sirketi", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-sigorta-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Plaka</Label>
                <Input
                  placeholder="34 ABC 123"
                  value={form.plaka}
                  onChange={(e) => set("plaka", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-plaka-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Dosya No</Label>
                <Input
                  placeholder="Dosya / hasar dosya no"
                  value={form.plaka_dosya_no}
                  onChange={(e) => set("plaka_dosya_no", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-dosya-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>İş Emri No</Label>
                <Input
                  placeholder="30-2026-10361"
                  value={form.is_emri_no}
                  onChange={(e) => set("is_emri_no", e.target.value)}
                  className="h-12"
                  data-testid="ciro-form-is-emri-no-input"
                />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label className="whitespace-nowrap">Belge No (Fatura No)</Label>
            <Input
              placeholder="FT2026..."
              value={form.belge_no}
              onChange={(e) => set("belge_no", e.target.value)}
              className="h-12"
              data-testid="ciro-form-belge-no-input"
            />
          </div>
        </div>
      </div>

      {/* ---- SINIFLANDIRMA & TAHSİLAT ---- */}
      <div className="space-y-3">
        <BolumBaslik icon={Layers}>Sınıflandırma ve Tahsilat</BolumBaslik>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Kanal *</Label>
            <Select value={form.kanal} onValueChange={(v) => set("kanal", v)}>
              <SelectTrigger className="h-12" data-testid="ciro-form-kanal-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {KANALLAR.map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isHizmet && !multiBolum && (
          <div className="space-y-1.5">
            <Label>Bölüm *</Label>
            <Select value={form.bolum} onValueChange={(v) => set("bolum", v)}>
              <SelectTrigger className="h-12" data-testid="ciro-form-bolum-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-card">
                {BOLUMLER.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
          {!isHizmet && (multiBolum ? bolumPaylar.some((p) => p.bolum === SSH) : isSSH) && (
            <div className="space-y-1.5">
              <Label>Alt Hizmet * <span className="text-xs text-muted-foreground">(SSH için zorunlu)</span></Label>
              <Select value={form.alt_hizmet} onValueChange={(v) => set("alt_hizmet", v)}>
                <SelectTrigger className="h-12" data-testid="ciro-form-alt-hizmet-select">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {ALT_HIZMETLER.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!(!editMode && form.aninda_tahsilat) && form.tahsilat_durumu !== "Bekliyor" && (
            <div className="space-y-1.5">
              <Label>Tahsilat Tarihi</Label>
              <Input
                type="date"
                value={form.tahsilat_tarihi}
                onChange={(e) => set("tahsilat_tarihi", e.target.value)}
                className="h-12"
                data-testid="ciro-form-tahsilat-tarihi-input"
              />
            </div>
          )}
        </div>

        {/* Bölüm Dağılımı: bir araçta birden fazla bölüm */}
        {!isHizmet && (
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3" data-testid="ciro-form-bolum-dagilim-blok">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Bölüm &amp; Usta Dağılımı</p>
                <p className="text-xs text-muted-foreground">
                  Bölüm seçilince o bölümün ustaları otomatik gelir. Bir araçta birden fazla bölüm çalıştıysa anahtarı açın — matrahı bölümlere, bölüm tutarını ustalara dağıtın (örn. 10.000 ₺ → 8.000 ₺ Kaporta + 2.000 ₺ Boya).
                </p>
                {aiBolumler.length > 1 && (
                  <p className="mt-0.5 text-[11px] font-medium text-sky-700" data-testid="ciro-form-ai-bolum-onerisi">
                    AI önerisi: {aiBolumler.join(", ")} — tutarları girip onaylayın veya düzeltin.
                  </p>
                )}
              </div>
              <Switch checked={multiBolum} onCheckedChange={setMultiBolum} data-testid="ciro-form-multi-bolum-switch" />
            </div>

            {/* TEK BÖLÜM MODU: bölüm seçilince o bölümün ustaları otomatik gelir */}
            {!multiBolum && isArac && form.bolum && (
              <div className="space-y-1.5" data-testid="ciro-form-tek-bolum-ustalar">
                <p className="text-[11px] font-medium text-muted-foreground">
                  {form.bolum} ustaları * — tıklayarak seçin (en az bir usta zorunlu):
                </p>
                {bolumUstalari(form.bolum).length === 0 ? (
                  <p className="text-[11px] text-amber-700" data-testid="ciro-form-tek-bolum-usta-bos">
                    Bu bölüme atanmış usta yok — Personel modülünden "Çalıştığı Bölümler" ataması yapın.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {bolumUstalari(form.bolum).map((u) => {
                      const secili = form.ustalar.some((x) => x.id === u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleUsta({ ...u, bolum: form.bolum })}
                          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                            secili
                              ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                              : "border-border bg-card text-foreground/80 hover:bg-muted/60"
                          }`}
                          data-testid={`ciro-form-usta-${u.id}`}
                        >
                          {secili ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                          {u.ad_soyad}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {multiBolum && (
              <div className="space-y-2">
                {bolumPaylar.map((p, i) => (
                  <div key={i} className="rounded-md border border-border/60 bg-card p-2.5 space-y-2" data-testid={`ciro-form-pay-satir-${i}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={p.bolum} onValueChange={(v) => paySet(i, "bolum", v)}>
                        <SelectTrigger className="h-11 w-52 bg-card" data-testid={`ciro-form-pay-bolum-${i}`}>
                          <SelectValue placeholder="Bölüm seçin" />
                        </SelectTrigger>
                        <SelectContent className="bg-card">
                          {BOLUMLER.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Tutar (KDV hariç)"
                        value={p.tutar}
                        onChange={(e) => paySet(i, "tutar", e.target.value)}
                        className="h-11 w-44 bg-card tabular-nums"
                        data-testid={`ciro-form-pay-tutar-${i}`}
                      />
                      {bolumPaylar.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-600" onClick={() => setBolumPaylar((a) => a.filter((_, x) => x !== i))} data-testid={`ciro-form-pay-sil-${i}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {p.bolum && (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-medium text-muted-foreground">{p.bolum} ustaları — tıklayarak seçin, tutarı ustalara dağıtın:</p>
                        {bolumUstalari(p.bolum).length === 0 ? (
                          <p className="text-[11px] text-amber-700" data-testid={`ciro-form-pay-usta-bos-${i}`}>
                            Bu bölüme atanmış usta yok — Personel modülünden "Çalıştığı Bölümler" ataması yapın.
                          </p>
                        ) : (
                        <div className="flex flex-wrap gap-1.5" data-testid={`ciro-form-pay-ustalar-${i}`}>
                          {bolumUstalari(p.bolum).map((u) => {
                            const secili = p.ustalar.some((x) => x.id === u.id);
                            return (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => payUstaToggle(i, u)}
                                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors duration-150 ${
                                  secili
                                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white"
                                    : "border-border bg-card text-foreground/80 hover:bg-muted/60"
                                }`}
                                data-testid={`ciro-form-pay-usta-${i}-${u.id}`}
                              >
                                {secili ? <X className="h-2.5 w-2.5" /> : <Plus className="h-2.5 w-2.5" />}
                                {u.ad_soyad}
                              </button>
                            );
                          })}
                        </div>
                        )}
                        {p.ustalar.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {p.ustalar.map((u) => (
                              <div key={u.id} className="flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-1">
                                <span className="text-[11px] font-medium">{u.ad_soyad}</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="₺"
                                  value={u.tutar}
                                  onChange={(e) => payUstaTutar(i, u.id, e.target.value)}
                                  className="h-8 w-28 bg-card text-xs tabular-nums"
                                  data-testid={`ciro-form-pay-usta-tutar-${i}-${u.id}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" variant="outline" size="sm" onClick={() => setBolumPaylar((a) => [...a, { bolum: "", tutar: "", ustalar: [] }])} className="gap-1.5 min-h-[40px]" data-testid="ciro-form-pay-ekle-button">
                    <Plus className="h-4 w-4" /> Bölüm Ekle
                  </Button>
                  {(() => {
                    const dagitilan = bolumPaylar.reduce((s, p) => s + (parseFloat(p.tutar) || 0), 0);
                    const kalan = (parseFloat(form.matrah) || 0) - dagitilan;
                    return (
                      <span className={`text-xs font-semibold tabular-nums ${kalan >= -0.01 ? "text-emerald-700" : "text-red-700"}`} data-testid="ciro-form-pay-ozet">
                        Dağıtılan: {dagitilan.toLocaleString("tr-TR")} ₺ • Kalan: {kalan.toLocaleString("tr-TR")} ₺ {kalan < -0.01 && "(matrahı geçemez!)"}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Ayrı usta bölümü kaldırıldı — ustalar bölüm seçilince otomatik gelir (tek bölümde aşağıda, kırılımda satır içinde) */}

      {/* ---- DIŞ KAYNAK HİZMETLERİ (Outsource) ---- */}
      {!isHizmet && (
        <div className="space-y-3" data-testid="ciro-form-dis-kaynak-blok">
          <BolumBaslik icon={Briefcase}>Dış Kaynak Hizmetleri (Outsource)</BolumBaslik>
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
            <p className="text-xs text-muted-foreground">
              Bu faturadaki dışarıya yaptırılan işler (örn. Turbo Revizyonu). Ciro tam tutarla kalır — dış kaynak tutarı cirodan düşülmez, ayrı maliyet satırı olarak izlenir ve bütçeye karışmaz.
            </p>
            {form.dis_kaynak.map((d, i) => (
              <div key={i} className="rounded-md border border-border/60 bg-card p-2.5 space-y-2" data-testid={`ciro-form-dk-satir-${i}`}>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="space-y-1">
                    <Label className="text-[11px]">Hizmet Adı *</Label>
                    <Input
                      value={d.hizmet_adi}
                      onChange={(e) => dkSet(i, "hizmet_adi", e.target.value)}
                      placeholder="örn. Turbo Revizyonu"
                      className="h-11 w-48"
                      data-testid={`ciro-form-dk-hizmet-${i}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">Tedarikçi</Label>
                    <Input
                      value={d.tedarikci}
                      onChange={(e) => dkSet(i, "tedarikci", e.target.value)}
                      placeholder="Opsiyonel"
                      className="h-11 w-36"
                      data-testid={`ciro-form-dk-tedarikci-${i}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">Matrah (KDV hariç) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={d.matrah}
                      onChange={(e) => dkSet(i, "matrah", e.target.value)}
                      className="h-11 w-32 tabular-nums"
                      data-testid={`ciro-form-dk-matrah-${i}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px]">KDV</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={d.kdv}
                      onChange={(e) => dkSet(i, "kdv", e.target.value)}
                      className="h-11 w-28 tabular-nums"
                      data-testid={`ciro-form-dk-kdv-${i}`}
                    />
                  </div>
                  <div className="space-y-1 pb-2.5">
                    <span className="text-xs font-semibold tabular-nums whitespace-nowrap" data-testid={`ciro-form-dk-satir-toplam-${i}`}>
                      Toplam: {((parseFloat(d.matrah) || 0) + (parseFloat(d.kdv) || 0)).toLocaleString("tr-TR")} ₺
                    </span>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="mb-1 h-9 w-9 text-muted-foreground hover:text-red-600" onClick={() => dkSil(i)} data-testid={`ciro-form-dk-sil-${i}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!d.faturali} onCheckedChange={(v) => dkSet(i, "faturali", v)} data-testid={`ciro-form-dk-faturali-${i}`} />
                    <span className="text-xs text-muted-foreground">{d.faturali ? "Faturalı (fatura yükleme zorunlu)" : "Faturasız"}</span>
                  </div>
                  {d.faturali && (
                    <div className="flex items-center gap-2">
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted/60">
                        <FileText className="h-3.5 w-3.5" />
                        {d._yukleniyor ? "Yükleniyor…" : d.fatura_dosya_id ? "Faturayı Değiştir" : "Fatura Yükle *"}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                          className="hidden"
                          onChange={(e) => dkDosyaYukle(i, e.target.files?.[0])}
                          data-testid={`ciro-form-dk-dosya-${i}`}
                        />
                      </label>
                      {d.fatura_dosya_id && (
                        <a
                          href={`${api.defaults.baseURL}/ciro/fatura-pdf/${d.fatura_dosya_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-medium text-[hsl(var(--primary))] underline underline-offset-2"
                          data-testid={`ciro-form-dk-dosya-link-${i}`}
                        >
                          {d.dosya_adi || "Yüklü faturayı gör"}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={dkEkle} className="gap-1.5 min-h-[40px]" data-testid="ciro-form-dk-ekle-button">
                <Plus className="h-4 w-4" /> Dış Kaynak Ekle
              </Button>
              {form.dis_kaynak.length > 0 && (
                <span className="text-xs font-semibold tabular-nums text-muted-foreground" data-testid="ciro-form-dk-toplam">
                  Dış Kaynak Toplamı (KDV dahil): {form.dis_kaynak.reduce((s, d) => s + (parseFloat(d.matrah) || 0) + (parseFloat(d.kdv) || 0), 0).toLocaleString("tr-TR")} ₺
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Anında tahsilat — yalnızca yeni kayıt eklerken */}
      {!editMode && (
        <div
          className={`rounded-lg border p-4 space-y-3 ${form.aninda_tahsilat ? "border-emerald-300 bg-emerald-50/50" : "border-border bg-muted/30"}`}
          data-testid="ciro-form-aninda-tahsilat-blok"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Banknote className={`h-4 w-4 ${form.aninda_tahsilat ? "text-emerald-700" : "text-muted-foreground"}`} />
              <div>
                <p className="text-sm font-medium">Anında Tahsil Edildi</p>
                <p className="text-xs text-muted-foreground">Fatura kayıt anında tahsil edildiyse açın — tahsilat kaydı otomatik oluşturulur.</p>
              </div>
            </div>
            <Switch
              checked={form.aninda_tahsilat}
              onCheckedChange={(v) => set("aninda_tahsilat", v)}
              data-testid="ciro-form-aninda-tahsilat-switch"
            />
          </div>
          {form.aninda_tahsilat && (
            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Tahsilatı Alan Kişi *</Label>
                <Input
                  placeholder="Ad Soyad"
                  value={form.tahsilat_alan_kisi}
                  onChange={(e) => set("tahsilat_alan_kisi", e.target.value)}
                  className="h-11 bg-card"
                  data-testid="ciro-form-tahsilat-alan-kisi-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nereye Ödendi *</Label>
                <Select value={form.tahsilat_odeme_yeri} onValueChange={(v) => set("tahsilat_odeme_yeri", v)}>
                  <SelectTrigger className="h-11 bg-card" data-testid="ciro-form-tahsilat-odeme-yeri-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {ODEME_YERLERI.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tahsilat Tarihi</Label>
                <Input
                  type="date"
                  value={form.tahsilat_tarihi || form.tarih}
                  onChange={(e) => set("tahsilat_tarihi", e.target.value)}
                  className="h-11 bg-card"
                  data-testid="ciro-form-aninda-tahsilat-tarihi-input"
                />
              </div>
              {form.tahsilat_odeme_yeri === "Tedarikçi Ödemesi" && (
                <div className="space-y-1.5 sm:col-span-3 xl:col-span-1">
                  <Label className="text-xs">Tedarikçi Adı *</Label>
                  {!yeniTedarikci && tedarikciler.length > 0 ? (
                    <div className="flex gap-2">
                      <Select value={form.tahsilat_tedarikci} onValueChange={(v) => set("tahsilat_tedarikci", v)}>
                        <SelectTrigger className="h-11 bg-card flex-1" data-testid="ciro-form-tedarikci-select">
                          <SelectValue placeholder="Tedarikçi seçin" />
                        </SelectTrigger>
                        <SelectContent className="bg-card">
                          {tedarikciler.map((t) => (
                            <SelectItem key={t.id} value={t.ad}>{t.ad}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 gap-1.5"
                        onClick={() => { setYeniTedarikci(true); set("tahsilat_tedarikci", ""); }}
                        data-testid="ciro-form-yeni-tedarikci-btn"
                      >
                        <Plus className="h-4 w-4" /> Yeni
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Tedarikçi adı yazın (listeye otomatik eklenir)"
                        value={form.tahsilat_tedarikci}
                        onChange={(e) => set("tahsilat_tedarikci", e.target.value)}
                        className="h-11 bg-card flex-1"
                        data-testid="ciro-form-tedarikci-input"
                      />
                      {tedarikciler.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11"
                          onClick={() => { setYeniTedarikci(false); set("tahsilat_tedarikci", ""); }}
                          data-testid="ciro-form-tedarikci-listeden-btn"
                        >
                          Listeden Seç
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Button
        type="submit"
        disabled={submitting}
        className="h-12 w-full sm:w-auto sm:min-w-[220px] rounded-lg text-base font-medium transition-colors duration-150 active:scale-[0.98]"
        data-testid="ciro-form-submit-button"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Kaydediliyor…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};
